import React, { FC, ReactElement, memo } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { clsx } from 'clsx';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import Button from '@mui/material/Button';
import { v4 as uuidv4 } from 'uuid';
// Component
import Space from '../layout/space';
import SvgIcon from '../icons/SvgIcon';
import Text, { TextComponentProps } from '../text';
import NameboardDetails from '../nameboardDetails';
// Resources
import { AddressType, MeetingPoint, Stop, StopType } from '../../core/apiTypes';
import { stringsRes } from '../../resources/strings';
import { getSuffixWithNumber } from '../../utils/strings';
import { formatDateFromTimestamp } from '../../utils/date';
import { IconAxel } from '../../resources/images';
import { dropOffGreen } from '../../resources/style/colors';
import { findStop } from '../../utils/business';

interface StopsTimeLineListProps {
    stops: Stop[];
    provision?: string | number;
    pancarte?: boolean;
    noTime?: boolean;
    noMeetingPoint?: boolean;
    noLabel?: boolean;
    onClick?: (stop: Stop) => void;
}

const StopsTimeLineList: FC<StopsTimeLineListProps> = ({
    stops,
    onClick,
    provision,
    pancarte,
    noTime = false,
    noMeetingPoint = false,
    noLabel = false,
}): ReactElement => {
    const classes = useStyles();

    // Colors
    const hourColor: TextComponentProps['customColor'] = 'grey';
    const textColor: TextComponentProps['customColor'] = 'black';
    const secondColor: TextComponentProps['customColor'] = 'grey';

    const formatedProvision = provision !== '' && provision !== null ? +provision : undefined;

    const showTime = !noTime && stops?.some((stop: Stop) => stop.driver_arrival_time);

    if (showTime && !stops.some((stop: Stop) => stop.stop_type === StopType.DRIVER_ARRIVAL)) {
        const initStop: Stop = {
            stop_order: -1,
            stop_type: StopType.DRIVER_ARRIVAL,
            eta: 0,
            connection_details: null,
            waiting_time: 0,
            payload: null,
            address: {
                name: stringsRes.booking.driverArrival,
                location: { lat: 0, lng: 0 },
                type: AddressType.ADDRESS,
                details: null,
            },
            driver_arrival_time: findStop(stops, StopType.PICKUP)?.driver_arrival_time,
        };
        stops.unshift(initStop);
    }

    const renderStopsTimeline = (): JSX.Element[] => {
        if (stops?.length > 0) {
            const hasDropOff = !!findStop(stops, StopType.DROP_OFF);
            const stopsArray = stops
                .sort((a: Stop, b: Stop) => {
                    if (a.stop_order && b.stop_order) {
                        return a.stop_order - b.stop_order;
                    }
                    return Object.keys(StopType).indexOf(a.stop_type) - Object.keys(StopType).indexOf(b.stop_type);
                })
                .map((stop: Stop, idx: number): JSX.Element => {
                    let meetingPoint: MeetingPoint = null;
                    let label = '';
                    if (showTime) {
                        if (stop.stop_type === StopType.PICKUP) {
                            label = stringsRes.common.startLabel;
                        } else if (stop.stop_type === StopType.DROP_OFF) {
                            label = stringsRes.common.endLabel;
                        }
                    }
                    if (stop.meeting_point_id) {
                        if (pancarte && stop.address?.meeting_points_nameBoard) {
                            meetingPoint = stop.address.meeting_points_nameBoard.find(
                                (m) => m.id === stop.meeting_point_id,
                            );
                        } else if (stop.address?.meeting_points) {
                            meetingPoint = stop.address.meeting_points.find((m) => m.id === stop.meeting_point_id);
                        }
                    }
                    return (
                        <TimelineItem className={classes.stopsTimeLineListItem} key={uuidv4()}>
                            <TimelineOppositeContent
                                className={showTime ? classes.oppositeContent : classes.noOppositeContent}
                            >
                                {showTime && (
                                    <div className={classes.stopsTimeLineListRowContainer}>
                                        {stop?.driver_arrival_time && (
                                            <Text.ShortRegularText14
                                                text={`${formatDateFromTimestamp(
                                                    idx > 0 && idx < stops.length - 1
                                                        ? stop.driver_departure_time
                                                        : stop.driver_arrival_time,
                                                    'HH:mm',
                                                )}`}
                                                customColor={hourColor}
                                            />
                                        )}
                                    </div>
                                )}
                            </TimelineOppositeContent>
                            <TimelineSeparator className={classes.separator}>
                                <TimelineDot variant="outlined">
                                    {stops?.length > 1 && stops.length - 1 === idx ? (
                                        <SvgIcon icon={IconAxel.DropOff} size="mediumSmall" stroke={dropOffGreen} />
                                    ) : (
                                        <SvgIcon
                                            icon={IconAxel.Pickup}
                                            size="mediumSmall"
                                            className={clsx(
                                                stop.stop_type === StopType.DRIVER_ARRIVAL && classes.arrivalIcon,
                                            )}
                                        />
                                    )}
                                </TimelineDot>
                                {(stops?.length - 1 !== idx ||
                                    (stops.length - 1 === idx && formatedProvision !== undefined && !hasDropOff)) && (
                                    <TimelineConnector className={classes.connector} />
                                )}
                            </TimelineSeparator>
                            <TimelineContent className={classes.content}>
                                <div className={classes.stopsTimeLineListRowContainer}>
                                    <Text.ShortRegularText14
                                        text={noLabel ? '' : label}
                                        customColor={textColor}
                                        secondText={stop?.address?.details?.full_name || stop.address.name}
                                        secondTextColor={secondColor}
                                    />
                                    {!noMeetingPoint &&
                                        stop.stop_type === StopType.PICKUP &&
                                        meetingPoint?.rdv_point && (
                                            <>
                                                <NameboardDetails
                                                    pancarte={pancarte}
                                                    meetingPoint={meetingPoint.rdv_point}
                                                />
                                                <Space value={1} />
                                            </>
                                        )}
                                    {onClick && (
                                        <Button
                                            className={classes.stopsTimeLineListButton}
                                            onClick={onClick.bind(this, stop)}
                                        >
                                            {stringsRes.booking.edit}
                                        </Button>
                                    )}
                                </div>
                                {/* TODO: add time when available from stop */}
                                {/* <Text.ShortRegularText12 text={'15:13'} customColor="grey" /> */}
                            </TimelineContent>
                        </TimelineItem>
                    );
                });

            if (formatedProvision !== undefined && !hasDropOff) {
                stopsArray.push(
                    <TimelineItem className={classes.stopsTimeLineListItem} key="provision">
                        <TimelineOppositeContent
                            className={showTime ? classes.oppositeContent : classes.noOppositeContent}
                        />
                        <TimelineSeparator className={classes.separator}>
                            <TimelineDot variant="outlined">
                                <SvgIcon icon={IconAxel.DropOff} size="mediumSmall" stroke={dropOffGreen} />
                            </TimelineDot>
                        </TimelineSeparator>
                        <TimelineContent className={classes.content}>
                            <div className={classes.stopsTimeLineListRowContainer}>
                                <Text.ShortRegularText14
                                    text={
                                        formatedProvision > 0
                                            ? `${stringsRes.booking.mad} - ${getSuffixWithNumber(
                                                  Math.floor(formatedProvision / 60),
                                                  stringsRes.time.hour,
                                                  stringsRes.time.hours,
                                              )}`
                                            : stringsRes.booking.freeRide
                                    }
                                    customColor={secondColor}
                                />
                            </div>
                        </TimelineContent>
                    </TimelineItem>,
                );
            }
            return stopsArray;
        }
        return null;
    };

    return (
        <div className={classes.stopsTimeLineListContainer}>
            <Timeline className={classes.timeline}>{renderStopsTimeline()}</Timeline>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        stopsTimeLineListContainer: {},
        timeline: {
            margin: 0,
            padding: 0,
        },
        dot: {
            padding: 1.5,
            borderWidth: 4,
            marginBottom: 0,
        },
        dotEnd: {
            padding: 3,
            borderWidth: 3,
            margin: '8.5px 0 0 0',
        },
        oppositeContent: {
            flex: 0,
            marginTop: -2,
            minWidth: 68,
        },
        noOppositeContent: {
            flex: 0,
            margin: 0,
            padding: 0,
            minWidth: 0,
            maxWidth: 0,
        },
        connector: {
            backgroundColor: theme.palette.colors.background.menuSeparator,
        },
        content: {
            flex: 1,
            flexDirection: 'column',
            display: 'flex',
            alignItems: 'flex-start',
            paddingTop: 4,
        },
        stopsTimeLineListRowContainer: {
            flex: 1,
            // width: '100%', // to always take max width
            flexDirection: 'row',
            justifyContent: 'space-between',
        },
        stopsTimeLineListItem: {
            minHeight: 'unset',
            '&:not(:first-child)': {
                paddingTop: 10,
            },
        },
        separator: {
            color: theme.palette.primary.dark,
            marginBottom: -11,
            '& .MuiTimelineDot-root': {
                border: 'none',
                padding: 0,
                margin: '5px 0px',
            },
        },
        stopsTimeLineListButton: {
            color: theme.palette.colors.text.overlineButton,
            fontWeight: 400,
            '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent',
            },
        },
        arrivalIcon: {
            '& circle': {
                fill: 'rgb(69, 112, 234)',
                stroke: 'white',
            },
        },
    }),
);

export default memo(StopsTimeLineList);
